import { Link } from "react-router-dom";
import { SAMPLE_EVALUATIONS, shouldUseSampleData } from "../data/sampleDashboardData";
import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import ReactApexChart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import { getDashboardOverview, getDashboardIntegration, getDashboardEvaluations, getDashboardBillingStatus } from "../services/dashboardApi";

const VERDICT_COLORS = {
  allowed: "#45B369",
  blocked: "#EF4A4A",
  review: "#F59E0B",
};

const SAMPLE_SUMMARY = {
total: 12455,
  allowed: 11836,
  blocked: 436,
  reviewed: 183,
};

const SAMPLE_ACTIVITY = {
  categories: [
    "22 Oct",
    "23 Oct",
    "24 Oct",
    "25 Oct",
    "26 Oct",
    "27 Oct",
    "28 Oct",
  ],
  allowed: [1480, 1590, 1510, 1720, 1650, 1810, 1760],
  review: [32, 41, 37, 48, 43, 55, 46],
  blocked: [71, 83, 76, 102, 94, 117, 108],
};

const SAMPLE_SIGNALS = [
  { label: "Bot behaviour", count: 186, width: 88 },
  { label: "VPN / Proxy", count: 143, width: 68 },
  { label: "Disposable email", count: 91, width: 43 },
  { label: "Repeat device", count: 72, width: 34 },
];

const SAMPLE_EVENTS = SAMPLE_EVALUATIONS.map((item) => ({
  id: item.id,
  email: item.email,
  risk: Math.round(Number(item.score || item.riskScore || 0) * 100),
  verdict: item.verdict,
  reasons: item.reasons || [],
  createdAt: item.createdAt,
}));

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("en-GB");

const formatRiskScore = (score) => {
  const numericScore = Number(score);

  if (!Number.isFinite(numericScore)) {
    return 0;
  }

  return Math.round(numericScore * 100);
};

const formatEventDate = (value) => {
  if (!value) {
    return {
      date: "-",
      time: "-",
    };
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return {
      date: "-",
      time: "-",
    };
  }

  return {
    date: parsed.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: parsed.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
  };
};

const readableReason = (reason) => {
  if (!reason) {
    return "";
  }

  return String(reason)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const MetricCard = ({
  title,
  value,
  icon,
  iconClass,
  footer = "All processed evaluations",
}) => (
  <div className="col-xxl-3 col-sm-6">
    <div className="card h-100 radius-8 border">
      <div className="card-body p-24">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <span className="text-secondary-light fw-medium">
              {title}
            </span>

            <h4 className="mb-0 mt-8">{value}</h4>
          </div>

          <span
            className={`w-48-px h-48-px rounded-circle d-flex justify-content-center align-items-center ${iconClass}`}
          >
            <Icon icon={icon} className="text-2xl" />
          </span>
        </div>

        <p className="text-sm text-secondary-light mt-12 mb-0">
          {footer}
        </p>
      </div>
    </div>
  </div>
);

const DashBoardLayerTwo = () => {
  const navigate = useNavigate();

  const [overview, setOverview] = useState(null);
  const [integration, setIntegration] = useState(null);
  const [analyticsEvaluations, setAnalyticsEvaluations] = useState([]);
  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;

    const loadDashboard = async () => {
      setLoading(true);
      setLoadError(null);

      try {
        const [data, integrationData, evaluationsData, billingData] = await Promise.all([
          getDashboardOverview(),
          getDashboardIntegration().catch(() => null),
          getDashboardEvaluations({ limit: 100 }),
          getDashboardBillingStatus().catch(() => null),
        ]);

        if (active) {
          setOverview(data);
          setIntegration(integrationData?.integration || null);
          setAnalyticsEvaluations(evaluationsData?.evaluations || []);
          setBillingStatus(billingData?.billing || null);
        }
      } catch (error) {
        if (!active) {
          return;
        }

        if (
          error?.status === 401 ||
          error?.code === "AUTH_REQUIRED"
        ) {
          navigate("/sign-in", { replace: true });
          return;
        }

        setLoadError(error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      active = false;
    };
  }, [navigate, reloadKey]);

  if (loading) {
    return (
      <section className="row gy-4">
        <div className="col-12">
          <div className="card radius-8 border">
            <div className="card-body p-24 text-center">
              <div
                className="spinner-border text-primary-600"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>

              <p className="text-secondary-light mt-16 mb-0">
                Loading your fraud dashboard...
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (loadError) {
    return (
      <section className="row gy-4">
        <div className="col-12">
          <div className="card radius-8 border">
            <div className="card-body p-24">
              <div className="d-flex align-items-start gap-3">
                <span className="w-48-px h-48-px rounded-circle bg-danger-focus text-danger-main d-flex justify-content-center align-items-center flex-shrink-0">
                  <Icon
                    icon="solar:danger-triangle-outline"
                    className="text-2xl"
                  />
                </span>

                <div>
                  <h6 className="mb-8">
                    We couldn't load your dashboard
                  </h6>

                  <p className="text-secondary-light mb-16">
                    {loadError.message ||
                      "Dashboard data is temporarily unavailable."}
                  </p>

                  <button
                    type="button"
                    className="btn btn-primary-600"
                    onClick={() =>
                      setReloadKey((current) => current + 1)
                    }
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  /*
   * Connection state comes from the backend integration record.
   * Evaluation count is not used to infer whether the API is connected.
   */
  const connected = integration?.connected === true;
  const hasRealEvaluations =
    Number(overview?.summary?.total || 0) > 0;
  const sampleMode = shouldUseSampleData(integration);

  const summary = sampleMode
    ? SAMPLE_SUMMARY
    : {
        total: Number(overview?.summary?.total || 0),
        allowed: Number(overview?.summary?.allowed || 0),
        blocked: Number(overview?.summary?.blocked || 0),
        reviewed: Number(overview?.summary?.reviewed || 0),
      };

  const activityByDate = analyticsEvaluations.reduce((days, evaluation) => {
    const rawDate = evaluation.createdAt || evaluation.created_at;
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return days;
    const key = date.toISOString().slice(0, 10);
    if (!days[key]) days[key] = { allowed: 0, review: 0, blocked: 0 };
    const verdict = String(evaluation.verdict || "").toUpperCase();
    if (verdict === "ALLOW") days[key].allowed += 1;
    if (verdict === "REVIEW") days[key].review += 1;
    if (verdict === "BLOCK") days[key].blocked += 1;
    return days;
  }, {});

  const activityDates = Object.keys(activityByDate).sort().slice(-7);
  const realActivity = {
    categories: activityDates.map((date) =>
      new Date(date + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
    ),
    allowed: activityDates.map((date) => activityByDate[date].allowed),
    review: activityDates.map((date) => activityByDate[date].review),
    blocked: activityDates.map((date) => activityByDate[date].blocked),
  };

  const riskActivityOptions = {
    chart: {
      type: "area",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },

    colors: [
      VERDICT_COLORS.allowed,
      VERDICT_COLORS.review,
      VERDICT_COLORS.blocked,
    ],

    dataLabels: {
      enabled: false,
    },

    stroke: {
      curve: "smooth",
      width: 2,
    },

    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.25,
        opacityTo: 0.04,
        stops: [0, 90, 100],
      },
    },

    markers: {
      size: 0,
    },

    xaxis: {
      categories: sampleMode ? SAMPLE_ACTIVITY.categories : realActivity.categories,
    },

    legend: {
      position: "top",
      horizontalAlign: "left",
    },

    grid: {
      strokeDashArray: 4,
    },

    noData: {
      text: "Historical activity analytics coming soon",
    },
  };

  const riskActivitySeries = sampleMode
    ? [
        {
          name: "Allowed",
          data: SAMPLE_ACTIVITY.allowed,
        },
        {
          name: "Review",
          data: SAMPLE_ACTIVITY.review,
        },
        {
          name: "Blocked",
          data: SAMPLE_ACTIVITY.blocked,
        },
      ]
    : [
        {
          name: "Allowed",
          data: realActivity.allowed,
        },
        {
          name: "Review",
          data: realActivity.review,
        },
        {
          name: "Blocked",
          data: realActivity.blocked,
        },
      ];

  const verdictOptions = {
    chart: {
      type: "donut",
    },

    colors: [
      VERDICT_COLORS.allowed,
      VERDICT_COLORS.blocked,
      VERDICT_COLORS.review,
    ],

    labels: [
      "Allowed",
      "Blocked",
      "Review",
    ],

    legend: {
      position: "bottom",
      fontSize: "11px",
    },

    dataLabels: {
      enabled: false,
    },

    plotOptions: {
      pie: {
        donut: {
          size: "72%",

          labels: {
            show: true,

            total: {
              show: true,
              label: "Evaluations",
              formatter: () => formatNumber(summary.total),
            },
          },
        },
      },
    },
  };

  const verdictSeries = [
    summary.allowed,
    summary.blocked,
    summary.reviewed,
  ];

  const realSignalCounts = analyticsEvaluations.reduce((counts, evaluation) => {
    (evaluation.reasonCodes || evaluation.reason_codes || []).forEach((reason) => {
      if (reason) counts[reason] = (counts[reason] || 0) + 1;
    });
    return counts;
  }, {});

  const realSignals = Object.entries(realSignalCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([reason, count], index, items) => ({
      label: readableReason(reason),
      count,
      width: Math.max(8, Math.round((count / items[0][1]) * 100)),
    }));

  const riskSignals = sampleMode ? SAMPLE_SIGNALS : realSignals;

  const events = sampleMode
    ? SAMPLE_EVENTS
    : (overview?.recentEvaluations || []).map((event) => {
        const formattedDate = formatEventDate(event.createdAt);

        return {
          id: event.id,
          email: event.signupEmail || "Unavailable",
          userId: event.id
            ? String(event.id).slice(0, 8)
            : "-",
          risk: formatRiskScore(event.score),
          verdict: String(
            event.verdict || "REVIEW"
          ).toUpperCase(),
          reason:
            Array.isArray(event.reasonCodes) &&
            event.reasonCodes.length > 0
              ? event.reasonCodes
                  .slice(0, 3)
                  .map(readableReason)
                  .join(", ")
              : "No major signals",
          date: formattedDate.date,
          time: formattedDate.time,
        };
      });

  const badgeClass = (verdict) => {
    if (verdict === "BLOCK") {
      return "bg-danger-focus text-danger-main";
    }

    if (verdict === "REVIEW") {
      return "bg-warning-focus text-warning-main";
    }

    return "bg-success-focus text-success-main";
  };

  const riskClass = (risk) => {
    if (risk >= 80) {
      return "fw-bold text-danger-main";
    }

    if (risk >= 50) {
      return "fw-bold text-warning-main";
    }

    return "fw-bold text-success-main";
  };

  const monthlyUsage = billingStatus?.usage || null;
  const usagePercentage = Number(monthlyUsage?.percentage || 0);
  const showUsageBanner = Boolean(monthlyUsage && usagePercentage >= 80);
  const usageLevel = usagePercentage >= 100 ? "limit" : usagePercentage >= 90 ? "urgent" : "warning";

  return (
    <section className="row gy-4">
      {showUsageBanner && (
        <div className="col-12">
          <div className={`alert ${usageLevel === "limit" ? "alert-danger" : "alert-warning"} d-flex flex-wrap align-items-center justify-content-between gap-3 mb-0`} role="alert">
            <div>
              <div className="fw-semibold mb-1">
                {usageLevel === "limit" ? "Monthly evaluation limit reached" : usageLevel === "urgent" ? "You are close to your monthly evaluation limit" : "You are approaching your monthly evaluation limit"}
              </div>
              <div>
                {Number(monthlyUsage.used).toLocaleString()} / {Number(monthlyUsage.limit).toLocaleString()} evaluations used ({Math.round(usagePercentage)}%). {usageLevel === "limit" ? "Upgrade your plan to continue processing signups." : "Upgrade your plan to avoid interruptions."}
              </div>
            </div>
            <Link to="/billing" className="btn btn-primary flex-shrink-0">Upgrade Plan</Link>
          </div>
        </div>
      )}

      <MetricCard
        title="Evaluations"
        value={formatNumber(summary.total)}
        icon="solar:shield-check-outline"
        iconClass="bg-primary-50 text-primary-600"
        footer={sampleMode ? "Sample data" : "All processed evaluations"}
      />

      <MetricCard
        title="Allowed"
        value={formatNumber(summary.allowed)}
        icon="solar:check-circle-outline"
        iconClass="bg-success-focus text-success-main"
        footer={sampleMode ? "Sample data" : "All processed evaluations"}
      />

      <MetricCard
        title="Blocked"
        value={formatNumber(summary.blocked)}
        icon="solar:shield-cross-outline"
        iconClass="bg-danger-focus text-danger-main"
        footer={sampleMode ? "Sample data" : "All processed evaluations"}
      />

      <MetricCard
        title="In Review"
        value={formatNumber(summary.reviewed)}
        icon="solar:clock-circle-outline"
        iconClass="bg-warning-focus text-warning-main"
        footer={sampleMode ? "Sample data" : "All processed evaluations"}
      />

      <div className="col-xxl-6 col-xl-12">
        <div className="card h-100 radius-8 border">
          <div className="card-body p-24">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-20">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h6 className="mb-2 fw-bold text-lg">
                    Risk Activity
                  </h6>

                  {sampleMode && (
                    <span className="badge bg-primary-50 text-primary-600">
                      SAMPLE
                    </span>
                  )}
                </div>

                <span className="text-sm text-secondary-light">
                  {sampleMode
                    ? "Example evaluation verdicts over 7 days"
                    : "Historical verdict analytics"}
                </span>
              </div>
            </div>

            <ReactApexChart
              options={riskActivityOptions}
              series={riskActivitySeries}
              type="area"
              height={300}
            />
          </div>
        </div>
      </div>

      <div className="col-xxl-3 col-md-6">
        <div className="card h-100 radius-8 border">
          <div className="card-body p-24">
            <div className="mb-24">
              <div className="d-flex align-items-center gap-2">
                <h6 className="mb-2 fw-bold text-lg">
                  Top Risk Signals
                </h6>

                {sampleMode && (
                  <span className="badge bg-primary-50 text-primary-600">
                    SAMPLE
                  </span>
                )}
              </div>

              <span className="text-sm text-secondary-light">
                {sampleMode
                  ? "Example risky signup signals"
                  : "Common risky signup signals"}
              </span>
            </div>

            {riskSignals.length > 0 ? (
              riskSignals.map((signal) => (
                <div className="mb-24" key={signal.label}>
                  <div className="d-flex justify-content-between align-items-center mb-8">
                    <span className="text-sm fw-medium text-primary-light">
                      {signal.label}
                    </span>

                    <span className="text-sm fw-semibold">
                      {signal.count}
                    </span>
                  </div>

                  <div className="progress progress-sm rounded-pill">
                    <div
                      className="progress-bar bg-primary-600 rounded-pill"
                      style={{
                        width: `${signal.width}%`,
                      }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-32">
                <Icon
                  icon="solar:chart-2-outline"
                  className="text-3xl text-secondary-light mb-8"
                />

                <p className="text-secondary-light mb-0">
                  Risk signal analytics will appear here as more
                  dashboard analytics become available.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="col-xxl-3 col-md-6">
        <div className="card h-100 radius-8 border">
          <div className="card-body p-24">
            <div className="mb-8">
              <div className="d-flex align-items-center gap-2">
                <h6 className="mb-2 fw-bold text-lg">
                  Verdict Distribution
                </h6>

                {sampleMode && (
                  <span className="badge bg-primary-50 text-primary-600">
                    SAMPLE
                  </span>
                )}
              </div>

              <span className="text-sm text-secondary-light">
                {sampleMode
                  ? "Example verdict distribution"
                  : "All processed evaluations"}
              </span>
            </div>

            <ReactApexChart
              options={verdictOptions}
              series={verdictSeries}
              type="donut"
              height={300}
            />
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="card radius-8 border">
          <div className="card-body p-24">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-20">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h6 className="mb-2 fw-bold text-lg">
                    Recent Events
                  </h6>

                  {sampleMode && (
                    <span className="badge bg-primary-50 text-primary-600">
                      SAMPLE
                    </span>
                  )}
                </div>

                <span className="text-sm text-secondary-light">
                  {sampleMode
                    ? "Example signup evaluations"
                    : "Latest signup evaluations processed by Simplif"}
                </span>
              </div>

              <button
                type="button"
                className="btn btn-outline-primary-600 btn-sm d-inline-flex align-items-center gap-2"
                onClick={() => setReloadKey((value) => value + 1)}
                disabled={loading}
              >
                <Icon icon="solar:refresh-outline" />
                Refresh
              </button>
            </div>

            <div className="w-100">
              <table
                className="table bordered-table mb-0"
                style={{
                  width: "100%",
                  tableLayout: "fixed",
                  fontSize: "13px",
                }}
              >
                <colgroup>
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "28%" }} />
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "7%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Signup Email</th>
                    <th>Risk Score</th>
                    <th>Verdict</th>
                    <th>Reasons</th>
                    <th>Date &amp; Time</th>
                    <th className="text-center">Details</th>
                  </tr>
                </thead>

                <tbody>
                  {events.length > 0 ? (
                    events.map((event) => (
                      <tr key={event.id}>
                        <td>
                          <span
                            className="fw-medium text-primary-light"
                            title={event.email}
                            style={{
                              display: "block",
                              width: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {event.email}
                          </span>
                        </td>

                        <td>
                          <span className={riskClass(event.risk)}>
                            {event.risk} / 100
                          </span>
                        </td>

                        <td>
                          <span
                            className={`px-12 py-4 radius-4 fw-medium text-sm ${badgeClass(
                              event.verdict
                            )}`}
                          >
                            {event.verdict}
                          </span>
                        </td>

                        <td
                          className="text-secondary-light"
                          title={event.reason}
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {event.reason}
                        </td>

                        <td>
                          <div className="d-flex flex-column">
                            <span className="fw-medium">
                              {event.date}
                            </span>

                            <span className="text-sm text-secondary-light">
                              {event.time}
                            </span>
                          </div>
                        </td>

                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary-600 d-inline-flex align-items-center justify-content-center"
                            onClick={() =>
                              navigate(`/events/${encodeURIComponent(event.id)}`)
                            }
                            aria-label={`View details for evaluation ${event.id}`}
                            title="View evaluation details"
                          >
                            <Icon icon="solar:info-circle-outline" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        className="text-center text-secondary-light py-32"
                      >
                        No evaluations have been processed yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashBoardLayerTwo;

